/**
 * ehr#191 - Care Team provider picker.
 *
 * A client's Demographics > Care Team entry can be filled from one of the practice's own
 * providers (GET /api/v1/providers/directory) instead of typed in. The entry keeps the
 * user id in `providerId`, and the entry ticked "Primary clinician" is put on new
 * appointments as their clinician.
 */
/** The Care Team "Role" choices (Demographics form). */
export const CARE_TEAM_ROLES = [
    'Primary care provider',
    'Psychiatrist',
    'Therapist / counselor',
    'Case manager',
    'Referring provider',
    'Specialist',
    'Nurse / NP',
    'Care coordinator',
    'Social worker',
    'Other',
];
function roleIn(text) {
    const t = (text ?? '').toLowerCase();
    if (!t.trim())
        return '';
    if (/psychiatr/.test(t))
        return 'Psychiatrist';
    if (/nurse|\bn\.?p\b|pmhnp|aprn|\brn\b/.test(t))
        return 'Nurse / NP';
    if (/case manag/.test(t))
        return 'Case manager';
    if (/coordinat/.test(t))
        return 'Care coordinator';
    if (/social work|\blcsw\b|\blmsw\b|\bmsw\b/.test(t))
        return 'Social worker';
    if (/therap|counsel|psycholog|\blpc|\blmft|\blmhc|\bmft\b|\bpsyd\b|clinician/.test(t))
        return 'Therapist / counselor';
    return '';
}
/**
 * The Care Team role a practice provider most likely holds, read from their title and
 * credentials (Provider Profile `title` / `cred` / `kind`). Blank when nothing fits, so the
 * form's Role stays for the user to pick. The title wins over the credentials: a
 * "Therapist, LCSW" is a therapist.
 */
export function careRoleFor(title, cred, kind) {
    const byTitle = roleIn(title);
    if (byTitle)
        return byTitle;
    const byCred = roleIn(cred);
    if (byCred)
        return byCred;
    if (kind === 'prescriber')
        return 'Psychiatrist';
    if (kind === 'licensed' || kind === 'associate')
        return 'Therapist / counselor';
    return '';
}
/** The Care Team entries of a Demographics record: `careteam` and `careteam#<n>`, in order. */
export function careTeamEntries(data) {
    return Object.keys(data)
        .filter((k) => k === 'careteam' || /^careteam#\d+$/.test(k))
        .sort((a, b) => a === 'careteam' ? -1 : b === 'careteam' ? 1 : Number(a.slice(9)) - Number(b.slice(9)))
        .map((k) => data[k] ?? {});
}
const norm = (s) => (s ?? '').trim().toLowerCase();
/**
 * The user id of the client's primary clinician: the Care Team entry ticked "Primary
 * clinician". A typed-in entry (no provider picked) still counts when its email, or its
 * first + last name, matches one of the practice's providers. '' when there is none.
 */
export function primaryClinicianId(data, providers) {
    const entry = careTeamEntries(data).find((e) => e.primary === 'true');
    if (!entry)
        return '';
    if (entry.providerId && providers.some((p) => p.id === entry.providerId))
        return entry.providerId;
    const email = norm(entry.email);
    const byEmail = email ? providers.find((p) => norm(p.email) === email) : undefined;
    if (byEmail)
        return byEmail.id;
    const first = norm(entry.firstName);
    const last = norm(entry.lastName);
    if (!first || !last)
        return '';
    const byName = providers.find((p) => norm(p.firstName) === first && norm(p.lastName) === last);
    return byName ? byName.id : '';
}
//# sourceMappingURL=care-team.js.map